import { useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { LeftNav } from "./LeftNav";
import { AIAssistantSidebar } from "./AIAssistantSidebar";
import { useSession } from "../lib/auth";
import { canSeeAdmin, canSeeAuditLog, canSeeDeletedEntities } from "../lib/permissions";
import { cn } from "../lib/cn";

/**
 * 3-column desktop layout, drawer + bottom-sheet on mobile.
 *
 *   Header (sticky, 56px)
 *   ┌────────────┬─────────────────────────┬───────────────┐
 *   │ LeftNav    │ <Outlet />              │ AIAssistant   │
 *   │ 224px w-56 │ flex-1                  │ 300px         │
 *   └────────────┴─────────────────────────┴───────────────┘
 *
 * Chat route hides the right rail (its main area is the chat itself).
 */
export function AppShell(): JSX.Element {
  const [navOpen, setNavOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const { user } = useSession();
  const location = useLocation();
  const showAssistant = location.pathname !== "/chat";

  return (
    <div className="min-h-screen bg-bg text-text">
      <Header
        onOpenNav={() => setNavOpen(true)}
        onOpenChat={() => setChatOpen(true)}
      />

      <div className="flex">
        <LeftNav
          open={navOpen}
          onClose={() => setNavOpen(false)}
          canSeeAdmin={canSeeAdmin(user?.role)}
          canSeeAuditLog={canSeeAuditLog(user?.role)}
          canSeeDeletedEntities={canSeeDeletedEntities(user?.role)}
        />

        <main
          id="main-content"
          className={cn(
            "flex-1 min-w-0 min-h-[calc(100vh-3.5rem)]",
            // Proto .main: padding: 20px 26px 56px
            "px-4 py-5 pb-14 sm:px-[26px]",
          )}
        >
          <Outlet />
        </main>

        {showAssistant && (
          <AIAssistantSidebar open={chatOpen} onClose={() => setChatOpen(false)} />
        )}
      </div>
    </div>
  );
}
